import { AnimatePresence, motion } from "framer-motion";
import type React from "react";
import type { Variants } from "framer-motion";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ReactionType } from "@/lib/humhub-api/types";
import {
  createEmojiFallbackDataUri,
  REACTION_ASSETS,
  REACTION_ORDER,
} from "@/lib/utils/reactionAssets";
import "@/index.css";

interface ReactionPopupProps {
  onReactionSelect: (reaction: ReactionType) => void;
  onReactionClear?: () => void;
  currentReaction?: ReactionType | null;
  isVisible: boolean;
}

// Helper function to create emoji JSX
const createEmojiElement = (type: ReactionType): React.ReactNode => {
  const asset = REACTION_ASSETS[type];
  const fallbackSvg = createEmojiFallbackDataUri(asset.fallbackEmoji);

  return (
    <img
      src={asset.src}
      alt={asset.alt}
      className="size-6"
      onError={(e) => {
        e.currentTarget.src = fallbackSvg;
      }}
    />
  );
};

const reactionEmojis: {
  type: ReactionType;
  emoji: string | React.ReactNode;
  label: string;
}[] = REACTION_ORDER.map((type) => ({
  type,
  emoji: createEmojiElement(type),
  label: REACTION_ASSETS[type].label,
}));

const containerVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      staggerChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    y: 10,
    transition: {
      duration: 0.2,
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

const emojiVariants: Variants = {
  hidden: {
    scale: 0,
    rotate: -180,
  },
  visible: {
    scale: 1,
    rotate: 0,
    transition: {
      type: "spring" as const,
      stiffness: 260,
      damping: 20,
    },
  },
  exit: {
    scale: 0,
    rotate: 180,
  },
  hover: {
    scale: 1.3,
    rotate: [0, -10, 10, -10, 10, 0],
    transition: {
      rotate: {
        duration: 0.4,
      },
    },
  },
  tap: {
    scale: 0.95,
    rotate: 0,
  },
};

export const ReactionPopup: React.FC<ReactionPopupProps> = ({
  onReactionSelect,
  onReactionClear,
  currentReaction = null,
  isVisible,
}) => {
  const { t } = useTranslation();
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="absolute bottom-full left-0 z-50 mb-2 flex w-fit gap-0.5 rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
        >
          {currentReaction && onReactionClear && (
            <motion.button
              variants={emojiVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onReactionClear();
              }}
              className="flex size-7 items-center justify-center rounded-full p-0.5 text-gray-500 hover:bg-gray-100"
              title={t("common.removeReaction", {
                defaultValue: "Remove reaction",
              })}
            >
              <Trash2 className="size-4" />
            </motion.button>
          )}
          {reactionEmojis.map(({ type, emoji, label }) => (
            <motion.button
              key={type}
              variants={emojiVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onReactionSelect(type);
              }}
              className="flex size-7 items-center justify-center rounded-full p-0.5 hover:bg-gray-100"
              title={label}
            >
              {emoji}
            </motion.button>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
